import React from "react";
import { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";

const EMPTY = { client_id: "", name: "", chart_type: "", currency: "XAF", tva_rate: "19.25", regime_fiscal: "reel_normal" };

export function AccFileFormDialog({ clients, onCreated }) {
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState(EMPTY);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        client_id: form.client_id,
        name: form.name || null,
        chart_type: form.chart_type || null,
        currency: form.currency,
        tva_rate: parseFloat(form.tva_rate) || 19.25,
        regime_fiscal: form.regime_fiscal,
      };
      const r = await api.post("/accounting/files", payload);
      toast.success("Dossier comptable créé");
      setOpen(false);
      setForm(EMPTY);
      mutate("/accounting/files");
      onCreated(r.data.id);
    } catch (err) { toast.error(err?.response?.data?.detail || "Création impossible"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-acc-file-btn" className="bg-[#2E3192] hover:bg-[#1E2066]"><Plus size={16} className="mr-1" /> Nouveau dossier</Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Nouveau dossier comptable</DialogTitle>
          <DialogDescription>Le plan comptable est proposé selon le secteur du client ; les journaux et l'exercice en cours sont créés automatiquement.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Client *</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger data-testid="accfile-client"><SelectValue placeholder="Sélectionner un client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name} · {c.country}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div><Label>Nom du dossier (facultatif)</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Comptabilité SARL X — 2026" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Plan comptable</Label>
              <Select value={form.chart_type || "auto"} onValueChange={(v) => setForm({ ...form, chart_type: v === "auto" ? "" : v })}>
                <SelectTrigger data-testid="accfile-chart"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Automatique (selon secteur)</SelectItem>
                  <SelectItem value="syscohada">SYSCOHADA révisé</SelectItem>
                  <SelectItem value="cobac">PCEC / COBAC (établissements de crédit)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Devise</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="XAF">FCFA (XAF)</SelectItem>
                  <SelectItem value="EUR">Euro (EUR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Taux TVA (%)</Label>
              <Input type="number" step="0.01" value={form.tva_rate} onChange={(e) => setForm({ ...form, tva_rate: e.target.value })} data-testid="accfile-tva" />
            </div>
            <div>
              <Label>Régime fiscal</Label>
              <Select value={form.regime_fiscal} onValueChange={(v) => setForm({ ...form, regime_fiscal: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="reel_normal">Réel normal</SelectItem>
                  <SelectItem value="reel_simplifie">Réel simplifié</SelectItem>
                  <SelectItem value="liberatoire">Libératoire</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end"><Button type="submit" disabled={!form.client_id} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="accfile-submit">Créer le dossier</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
